import {ref, shallowRef} from 'vue';
import {defineStore} from 'pinia';

import breadcrumbsBuilder from '@/core/utils/breadcrumbs';
import {getAppContext} from '@/core/plugins/appContext';

export const useNavigationStore = defineStore('navigation', () => {
    const {$api} = getAppContext();

    const menu = ref([]);
    const showSubMenuState = ref([]);
    const activeChapter = ref(null);
    const activeExpandItem = ref(null);
    const showTopMenu = shallowRef(false);
    const activeSubMenu = shallowRef('');
    const breadcrumbs = ref(null);
    const hideBreadcrumbs = ref(false);

    function setMenu(payload) {
        menu.value = payload;
    }

    function setBreadcrumbs(crumbs) {
        breadcrumbs.value = crumbs;
    }

    function setBreadcrumbsSlug(title, force = false) {
        breadcrumbs.value = breadcrumbs.value.map((crumb) => {
            if (!crumb.title) {
                crumb.title = title;
            }

            return crumb;
        }).reduce((acc, item, index) => {
            if (!acc.length) {
                acc.push(item);

                return acc;
            }

            const isContainSameCrumb = acc.some((crumb) => crumb.title.toLowerCase() === item.title.toLowerCase());

            if (!isContainSameCrumb || force) {
                acc.push(item);
            } else {
                acc[index - 1].slug = '';
            }

            return acc;
        }, []);
    }

    function setActiveChapter(chapter) {
        if (activeChapter.value !== chapter) {
            activeChapter.value = chapter;
        }
    }

    function toggleSubMenu(slug = '') {
        if (activeSubMenu.value !== slug) {
            activeSubMenu.value = slug;
            activeExpandItem.value = null;

            return;
        }

        activeSubMenu.value = '';
        activeExpandItem.value = null;
    }

    function toggleExpandItem(item = null) {
        if (!activeExpandItem.value) {
            activeExpandItem.value = item;

            return;
        }

        activeExpandItem.value = item && activeExpandItem.value.title !== item.title ? item : null;
    }

    async function getMenu() {
        if (!menu.value.length) {
            try {
                const {data: menu} = await $api.common.menu.get();
                setMenu(menu);

                return menu;
            } catch (error) {
                console.error(error);
            }
        }
    }

    function getBreadcrumbs({route, data}) {
        return new Promise((resolve) => {
            const crumbs = breadcrumbsBuilder({
                currentRoute: route,
                menu: menu.value,
                ...data,
            }) ?? null;

            setBreadcrumbs(crumbs);
            resolve(crumbs);
        });
    }

    function updateBreadcrumbsSlug(title, ...args) {
        return new Promise((resolve) => {
            setBreadcrumbsSlug(title, ...args);
            resolve(breadcrumbs.value);
        });
    }

    return {
        menu,
        showSubMenuState,
        activeChapter,
        activeExpandItem,
        showTopMenu,
        activeSubMenu,
        breadcrumbs,
        hideBreadcrumbs,
        setMenu,
        setBreadcrumbs,
        setBreadcrumbsSlug,
        setActiveChapter,
        toggleSubMenu,
        toggleExpandItem,
        getMenu,
        getBreadcrumbs,
        updateBreadcrumbsSlug,
    };
});
